import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile,
  User as FirebaseUser,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithCredential,
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db, ADMIN_EMAIL, getStagingAllowedEmails } from '../firebase/config';
import { trackLogin, trackSignUp, trackLogout, setAnalyticsUser } from '../services/analyticsService';
import { recordLogin } from '../services/loginAuditService';
import { isStaging } from '../utils/environment';

interface User {
  id: string;
  email: string;
  name: string;
  /** Optional user-chosen display name shown in the app (overrides `name`). */
  alias?: string;
  avatar?: string;
  /** IANA timezone for DISPLAY only (e.g. "Europe/London"); backend always stores UTC. Default UTC. */
  timezone?: string;
  domain: string;
  isAdmin: boolean;
  createdAt: Date;
  lastActive: Date;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  /** Complete a sign-in from a Google One Tap credential — the same pipeline, a different door. */
  signInWithGoogleCredential: (idToken: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  getIdToken: () => Promise<string | null>;
  /** Set the user's display alias (persisted to their profile; '' clears it). */
  updateAlias: (alias: string) => Promise<void>;
  /** Set the user's display timezone (IANA id; '' resets to UTC). Display-only — backend stays UTC. */
  updateTimezone: (timezone: string) => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/** Admin flag that does NOT throw outside an AuthProvider (returns false) — for components that can
 *  render in isolation / unit tests without the provider (e.g. AgentDetailPanel inside ChatThread). */
export const useIsAdmin = (): boolean => useContext(AuthContext)?.isAdmin ?? false;

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const extractDomain = (email: string): string => {
    const parts = email.split('@');
    return parts.length === 2 ? parts[1] : '';
  };

  const isAdmin = (email: string): boolean => {
    return email === ADMIN_EMAIL;
  };

  const getAuthErrorMessage = (errorCode: string): string => {
    switch (errorCode) {
      case 'auth/user-not-found':
        return 'No account found with this email address.';
      case 'auth/wrong-password':
        return 'Incorrect password.';
      case 'auth/email-already-in-use':
        return 'An account with this email already exists.';
      case 'auth/weak-password':
        return 'Password should be at least 6 characters.';
      case 'auth/invalid-email':
        return 'Invalid email address.';
      case 'auth/user-disabled':
        return 'This account has been disabled.';
      case 'auth/popup-closed-by-user':
        return 'Sign-in was cancelled.';
      default:
        return 'An error occurred during authentication.';
    }
  };

  const enforceStagingAllowlist = async (firebaseUser: FirebaseUser) => {
    if (!isStaging()) return;
    const email = firebaseUser.email?.toLowerCase();
    if (!email || !getStagingAllowedEmails().includes(email)) {
      await firebaseSignOut(auth);
      throw new Error('Access restricted. This staging environment is limited to the team.');
    }
  };

  const createUserProfile = async (firebaseUser: FirebaseUser, name: string): Promise<User> => {
    const email = firebaseUser.email!;
    const domain = extractDomain(email);
    const adminUser = isAdmin(email);

    const userProfile: User = {
      id: firebaseUser.uid,
      email,
      name: name || email.split('@')[0],
      domain,
      isAdmin: adminUser,
      avatar: firebaseUser.photoURL || undefined,
      createdAt: new Date(),
      lastActive: new Date(),
    };

    // Firestore REJECTS `undefined` field values (CLAUDE.md Firestore gotcha), and `avatar` is undefined for
    // any account without a photoURL — an email/password signup, or a Google account with no picture. Spreading
    // it straight in made `setDoc` throw for exactly those users, and because the catch below swallows the
    // error they ended up authenticated with NO user document: silently locked out of every gated page, with
    // nothing but a console line to show for it. Omit the key instead of writing undefined.
    const { avatar, ...profileFields } = userProfile;
    try {
      await setDoc(doc(db, 'users', firebaseUser.uid), {
        ...profileFields,
        ...(avatar ? { avatar } : {}),
        createdAt: serverTimestamp(),
        lastActive: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error creating user profile in Firestore:', error);
      // Continue without storing in Firestore - user can still use the app
    }

    return userProfile;
  };

  const getUserProfile = async (userId: string): Promise<User | null> => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const data = userDoc.data();
        return {
          ...data,
          // A user's own generated/uploaded picture lives at users/{uid}.photo_url and overrides
          // the Google/social default everywhere it's shown.
          avatar: data.photo_url || (data as any).avatar,
          createdAt: data.createdAt?.toDate() || new Date(),
          lastActive: data.lastActive?.toDate() || new Date(),
        } as User;
      }
      return null;
    } catch (error) {
      console.error('Error getting user profile:', error);
      return null;
    }
  };

  useEffect(() => {

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          await enforceStagingAllowlist(firebaseUser);
        } catch {
          setUser(null);
          setLoading(false);
          return;
        }
        let userProfile = await getUserProfile(firebaseUser.uid);
        if (!userProfile) {
          // Create profile from Firebase Auth data
          userProfile = await createUserProfile(firebaseUser, firebaseUser.displayName || '');
        }
        setUser(userProfile);
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = async (email: string, password: string) => {
    setError(null);
    try {
      const { user: firebaseUser } = await signInWithEmailAndPassword(auth, email, password);
      await enforceStagingAllowlist(firebaseUser);

      let userProfile = await getUserProfile(firebaseUser.uid);

      if (!userProfile) {
        userProfile = await createUserProfile(firebaseUser, firebaseUser.displayName || '');
      }

      setUser(userProfile);

      // Track login event
      trackLogin('email');
      setAnalyticsUser(userProfile.id, userProfile.email, userProfile.name);
      recordLogin(); // system audit: email + masked IP + geo + device (fire-and-forget)
    } catch (err: any) {
      const errorMessage = err.code ? getAuthErrorMessage(err.code) : (err.message || 'Authentication failed');
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const signUp = async (email: string, password: string, name: string) => {
    setError(null);
    try {
      const { user: firebaseUser } = await createUserWithEmailAndPassword(auth, email, password);
      await enforceStagingAllowlist(firebaseUser);
      await updateProfile(firebaseUser, { displayName: name });
      const userProfile = await createUserProfile(firebaseUser, name);
      setUser(userProfile);

      // Track sign up event
      trackSignUp('email');
      setAnalyticsUser(userProfile.id, userProfile.email, userProfile.name);
    } catch (err: any) {
      const errorMessage = err.code ? getAuthErrorMessage(err.code) : (err.message || 'Authentication failed');
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  /**
   * EVERYTHING THAT HAPPENS AFTER GOOGLE SAYS YES — shared by the popup and by One Tap.
   *
   * Extracted rather than duplicated because one of the steps is a SECURITY control: `enforceStagingAllowlist`
   * signs out any account not on the team list, and a second sign-in route that forgot it would be a way into
   * staging that the first route closes. The allowlist, the profile creation, the analytics identity and the
   * login audit all belong to "a Google sign-in completed", not to the button that started it.
   */
  const completeGoogleSignIn = async (firebaseUser: FirebaseUser, method: 'google' | 'google_one_tap') => {
    await enforceStagingAllowlist(firebaseUser);

    let userProfile = await getUserProfile(firebaseUser.uid);
    const isNewUser = !userProfile;
    if (!userProfile) {
      userProfile = await createUserProfile(firebaseUser, firebaseUser.displayName || '');
    }
    setUser(userProfile);

    // The METHOD is carried through so One Tap's conversion can be read separately from the button's —
    // otherwise we ship a funnel change we cannot measure.
    if (isNewUser) {
      trackSignUp(method);
    } else {
      trackLogin(method);
    }
    setAnalyticsUser(userProfile.id, userProfile.email, userProfile.name);
    recordLogin(); // system audit: email + masked IP + geo + device (fire-and-forget)
  };

  const signInWithGoogle = async () => {
    setError(null);

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      await completeGoogleSignIn(result.user, 'google');
    } catch (err: any) {
      console.error('[Auth] Error in signInWithGoogle:', err);
      const errorMessage = err.code ? getAuthErrorMessage(err.code) : (err.message || 'Authentication failed');
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  /**
   * SIGN IN FROM A ONE TAP CREDENTIAL.
   *
   * One Tap hands back a Google ID token rather than opening a popup, so the exchange happens here and the
   * result goes through the SAME pipeline as every other Google sign-in. Nothing about being one tap instead
   * of two relaxes what happens afterwards.
   */
  const signInWithGoogleCredential = async (idToken: string) => {
    setError(null);
    try {
      const result = await signInWithCredential(auth, GoogleAuthProvider.credential(idToken));
      await completeGoogleSignIn(result.user, 'google_one_tap');
    } catch (err: any) {
      console.error('[Auth] Error in One Tap sign-in:', err);
      const errorMessage = err.code ? getAuthErrorMessage(err.code) : (err.message || 'Authentication failed');
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const signOut = async () => {
    setError(null);
    try {
      trackLogout(); // Track before signing out

      // Clear all localStorage and sessionStorage
      try {
        localStorage.clear();
        sessionStorage.clear();
      } catch (storageError) {
        console.warn('Failed to clear browser storage:', storageError);
      }

      await firebaseSignOut(auth);
      setUser(null);
    } catch (err: any) {
      const errorMessage = 'Failed to sign out. Please try again.';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const resetPassword = async (email: string) => {
    setError(null);
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (err: any) {
      const errorMessage = getAuthErrorMessage(err.code);
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const updateAlias = async (alias: string): Promise<void> => {
    if (!user) return;
    const clean = alias.trim().slice(0, 40);
    // Persist to the user's own profile doc (same client-write path as createUserProfile).
    try {
      await setDoc(doc(db, 'users', user.id), { alias: clean || null }, { merge: true });
    } catch (error) {
      console.warn('Failed to save alias:', error);
    }
    setUser({ ...user, alias: clean || undefined });
  };

  const updateTimezone = async (timezone: string): Promise<void> => {
    if (!user) return;
    const clean = (timezone || '').trim() || 'UTC';
    // Persist to the user's own profile doc (same client-write path as updateAlias). Display-only.
    try {
      await setDoc(doc(db, 'users', user.id), { timezone: clean }, { merge: true });
    } catch (error) {
      console.warn('Failed to save timezone:', error);
    }
    setUser({ ...user, timezone: clean });
  };

  /** Re-fetch the profile doc (e.g. after generating a new avatar) so the UI reflects it. */
  const refreshProfile = async (): Promise<void> => {
    if (!auth.currentUser) return;
    const fresh = await getUserProfile(auth.currentUser.uid);
    if (fresh) setUser(fresh);
  };

  const getIdToken = async (): Promise<string | null> => {
    try {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        return null;
      }
      return await firebaseUser.getIdToken();
    } catch (error) {
      console.error('Error getting ID token:', error);
      return null;
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    error,
    isAdmin: user?.isAdmin || false,
    signIn,
    signUp,
    signInWithGoogle,
    signInWithGoogleCredential,
    signOut,
    resetPassword,
    getIdToken,
    updateAlias,
    updateTimezone,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

